import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  CircularProgress, Alert, Divider, Stack, Avatar,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';

const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE_MB    = 5;

const initialForm = {
  name: '',
  designation: '',
  employee_email: '',
  email_subject: '',
  email_body: '',
};

const AdminJoineeBanner = () => {
  const { getAuthHeaders } = useAuth();

  const [form,           setForm]           = useState(initialForm);
  const [photo,          setPhoto]          = useState(null);       // File object
  const [preview,        setPreview]        = useState(null);       // Data URL
  const [loading,        setLoading]        = useState(false);
  const [bannerGenerated, setBannerGenerated] = useState(null);     // { bannerUrl, emailHtml, emailSubject } after generation
  const [htmlCopied,     setHtmlCopied]     = useState(false);
  const [error,          setError]          = useState('');

  const fileInputRef = useRef(null);

  const handleFormChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileSelect = useCallback((file) => {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Only JPG and PNG images are accepted.');
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Photo must be under ${MAX_SIZE_MB} MB.`);
      return;
    }
    setError('');
    setPhoto(file);
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  }, []);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer?.files?.[0]);
  }, [handleFileSelect]);

  const handleReset = () => {
    setForm(initialForm);
    setPhoto(null);
    setPreview(null);
    setBannerGenerated(null);
    setHtmlCopied(false);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCopyHtml = () => {
    if (!bannerGenerated?.emailHtml) {
      console.error('No HTML content to copy:', bannerGenerated);
      toast.error('HTML template not available. Please generate it first.');
      return;
    }
    
    const html = bannerGenerated.emailHtml;
    
    // Create a blob and use the Clipboard API with HTML format
    const blob = new Blob([html], { type: 'text/html' });
    const data = [new ClipboardItem({ 'text/html': blob })];
    
    navigator.clipboard.write(data)
      .then(() => {
        console.log('HTML template copied with formatting!');
        setHtmlCopied(true);
        toast.success('Email template copied! Now paste directly into Outlook.');
        setTimeout(() => setHtmlCopied(false), 3000);
      })
      .catch((err) => {
        console.error('Copy failed:', err);
        // Fallback: copy as plain text
        navigator.clipboard.writeText(html)
          .then(() => {
            toast.success('HTML code copied as text. Paste into Outlook HTML editor.');
          })
          .catch(() => {
            toast.error('Failed to copy. Please download the file instead.');
          });
      });
  };

  const handleDownloadTemplate = () => {
    if (!bannerGenerated?.emailHtml) {
      toast.error('Email template not available.');
      return;
    }
    const element = document.createElement('a');
    const file = new Blob([bannerGenerated.emailHtml], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    element.download = `welcome_email_${form.name.replace(/\s+/g, '_')}_${Date.now()}.html`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Email template downloaded! Open in browser and copy-paste into Outlook.');
  };

  const handleGenerateBanner = async (e) => {
    e.preventDefault();
    setError('');

    if (!photo) {
      setError('Please upload an employee photo.');
      return;
    }
    if (!form.name.trim() || !form.designation.trim() || !form.employee_email.trim()) {
      setError('Name, designation, and employee email are required.');
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.employee_email.trim());
    if (!emailOk) {
      setError('Please enter a valid email address.');
      return;
    }
    const fd = new FormData();
    fd.append('photo', photo);
    fd.append('name', form.name.trim());
    fd.append('designation', form.designation.trim());
    fd.append('employee_email', form.employee_email.trim());
    fd.append('email_subject', form.email_subject.trim());
    fd.append('email_body', form.email_body);

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}/joinee-banner/generate`, fd, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes for image processing + upload
      });

      console.log('Banner generation response:', response.data);
      setBannerGenerated({
        bannerUrl: response.data.bannerUrl,
        emailHtml: response.data.emailHtml,
        emailSubject: response.data.emailSubject,
      });
      setError('');
      toast.success('Banner and email template generated successfully!');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Something went wrong.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        New Joinee Banner &amp; Welcome Email
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload the employee&apos;s photo and fill in their details. A branded welcome banner
        and a complete HTML email template will be generated for you to copy and paste into your email client.
      </Typography>

      <Grid container spacing={3}>
        {/* ── Left: Form ── */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
            <form onSubmit={handleGenerateBanner} noValidate>
              {/* Photo upload area */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Employee Photo <span style={{ color: 'red' }}>*</span>
              </Typography>
              <Box
                onDrop={handleFileDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: preview ? 'success.main' : 'primary.main',
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: preview ? 'success.50' : 'grey.50',
                  transition: 'all 0.2s',
                  '&:hover': { bgcolor: 'primary.50' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'center',
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
                {preview ? (
                  <>
                    <Avatar
                      src={preview}
                      sx={{ width: 72, height: 72, border: '2px solid', borderColor: 'success.main' }}
                    />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        {photo?.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Click to change
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <CloudUploadIcon color="primary" sx={{ fontSize: 40, mb: 0.5 }} />
                    <Typography variant="body2" color="primary">
                      Click or drag &amp; drop photo here
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      JPG / PNG, max {MAX_SIZE_MB} MB
                    </Typography>
                  </Box>
                )}
              </Box>

              <Stack spacing={2}>
                <TextField
                  label="Employee Name"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                  fullWidth
                  inputProps={{ maxLength: 80 }}
                  placeholder="e.g. George Nadvi"
                />
                <TextField
                  label="Designation / Job Title"
                  name="designation"
                  value={form.designation}
                  onChange={handleFormChange}
                  required
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                  placeholder="e.g. Sr. Research Manager"
                />
                <TextField
                  label="Employee Email"
                  name="employee_email"
                  type="email"
                  value={form.employee_email}
                  onChange={handleFormChange}
                  required
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                  placeholder="employee@divergentinsights.com"
                />

                <Divider sx={{ my: 0.5 }} />
                <Typography variant="subtitle2" color="text.secondary">
                  Email Template Content (optional)
                </Typography>

                <TextField
                  label="Email Subject"
                  name="email_subject"
                  value={form.email_subject}
                  onChange={handleFormChange}
                  fullWidth
                  inputProps={{ maxLength: 160 }}
                  placeholder={`Welcome to the Team – ${form.name || '<Name>'}`}
                />
                <TextField
                  label="Email Body Message"
                  name="email_body"
                  value={form.email_body}
                  onChange={handleFormChange}
                  fullWidth
                  multiline
                  minRows={4}
                  placeholder="Write a welcome message here. Basic HTML tags like <b>, <p>, <br> are supported."
                  helperText="Appears below the banner in the generated HTML template."
                />
              </Stack>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  {loading ? 'Generating\u2026' : bannerGenerated ? 'Regenerate' : 'Generate Template'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Stack>
            </form>
          </Paper>
        </Grid>

        {/* ── Right: Preview / Result ── */}
        <Grid item xs={12} md={5}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: bannerGenerated ? 'flex-start' : 'center',
              minHeight: 320,
              bgcolor: bannerGenerated ? 'info.50' : 'grey.50',
              border: bannerGenerated ? '1.5px solid' : 'none',
              borderColor: bannerGenerated ? 'info.light' : 'transparent',
            }}
          >
            {bannerGenerated ? (
              <Box sx={{ width: '100%' }}>
                <Typography variant="h6" fontWeight={700} color="primary.dark" textAlign="center" sx={{ mb: 2 }}>
                  Template Ready
                </Typography>

                <Box
                  component="img"
                  src={bannerGenerated.bannerUrl}
                  alt="Generated welcome banner"
                  sx={{ width: '100%', borderRadius: 1, boxShadow: 3, mb: 2 }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  href={bannerGenerated.bannerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ mb: 2 }}
                >
                  Open Banner in New Tab
                </Button>

                <Divider sx={{ mb: 2 }} />

                {/* Email Subject */}
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      EMAIL SUBJECT
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={() => {
                        navigator.clipboard.writeText(bannerGenerated.emailSubject);
                        toast.success('Subject copied!');
                      }}
                    >
                      Copy
                    </Button>
                  </Stack>
                  <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, p: 1, border: '1px solid', borderColor: 'grey.300' }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {bannerGenerated.emailSubject}
                    </Typography>
                  </Box>
                </Box>

                {/* HTML Template Preview */}
                <Box sx={{ width: '100%' }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      EMAIL TEMPLATE PREVIEW
                    </Typography>
                  </Stack>
                  {bannerGenerated?.emailHtml ? (
                    <>
                      <Box
                        component="iframe"
                        srcDoc={bannerGenerated.emailHtml}
                        sx={{
                          width: '100%',
                          height: '250px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: '#fff',
                          marginBottom: '12px',
                        }}
                      />
                      <Stack spacing={1}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="success"
                          startIcon={<ContentCopyIcon />}
                          onClick={handleCopyHtml}
                        >
                          Copy & Paste to Outlook
                        </Button>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                          Click above, then paste (Ctrl+V) directly in a new Outlook email body.
                        </Typography>
                      </Stack>
                    </>
                  ) : (
                    <Typography variant="caption" color="error">
                      Email template not generated. Please try again.
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', color: 'text.disabled' }}>
                <CloudUploadIcon sx={{ fontSize: 64, mb: 1, opacity: 0.3 }} />
                <Typography variant="body2">
                  Fill in the form and click&nbsp;
                  <strong>Generate Template</strong>&nbsp;
                  to compose the banner and HTML email template ready to copy and paste.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminJoineeBanner;
